'use client';

import { defaultShouldDehydrateQuery, QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { useState, useEffect, useRef, useMemo } from 'react';
import { isLikelyAuthFailure, tryBeginSessionTermination } from '@/lib/auth/sessionTermination';
import { clearAllUserData } from '@/lib/utils/clearUserData';
import { useUserId } from '@/lib/hooks/useUserId';
import { ReactQueryDevtoolsLazy } from '@/lib/providers/ReactQueryDevtoolsLazy';
import { createTanstackIndexedPersister } from '@/lib/persistence/queryPersister';

interface QueryProviderProps {
  children: React.ReactNode;
}

const PERSIST_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function useQueryBuster(): string {
  return process.env.NEXT_PUBLIC_APP_VERSION ?? 'development';
}

export function QueryProvider({ children }: QueryProviderProps) {
  const userId = useUserId();
  const previousUserIdRef = useRef<string | null>(null);

  const handle401Error = (error: unknown) => {
    if (!isLikelyAuthFailure(error)) return false;

    if (tryBeginSessionTermination()) {
      window.dispatchEvent(
        new CustomEvent('auth:logout', {
          detail: { reason: '401_unauthorized' },
        }),
      );

      import('@/app/actions/auth')
        .then(({ clearAuthCookiesAction }) => {
          clearAuthCookiesAction().catch(() => {});
        })
        .catch(() => {});

      window.location.href = '/login';
    }
    return true;
  };

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 0,
            gcTime: 600_000,
            retry: (failureCount, error) => {
              if (handle401Error(error)) return false;
              if (error instanceof Error && error.message.includes('4')) return false;
              return failureCount < 3;
            },
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
            refetchOnWindowFocus: true,
            refetchOnMount: true,
            refetchOnReconnect: true,
          },
          mutations: {
            retry: (failureCount, error) => {
              if (handle401Error(error)) return false;
              if (error instanceof Error && error.message.includes('4')) return false;
              return failureCount < 2;
            },
            onError: (error) => {
              handle401Error(error);
            },
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
          },
        },
      }),
  );

  const persister = useMemo(() => createTanstackIndexedPersister(), []);
  const buster = useQueryBuster();

  useEffect(() => {
    if (!userId) return;

    void import('@/lib/persistence/db').then(({ setCachedUserId, requestPersistentStorage }) => {
      setCachedUserId(userId).catch(() => {});
      requestPersistentStorage(userId).catch(() => {});
    });
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    if (previousUserIdRef.current && previousUserIdRef.current !== userId) {
      clearAllUserData(queryClient);
    }
    previousUserIdRef.current = userId;
  }, [queryClient, userId]);

  useEffect(() => {
    const handleLogout = () => {
      clearAllUserData(queryClient);
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, [queryClient]);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        persister,
        maxAge: PERSIST_MAX_AGE_MS,
        buster,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            const root = query.queryKey[0];
            if (typeof root !== 'string') return false;
            if (
              root !== 'trips' &&
              root !== 'wishlist' &&
              root !== 'places' &&
              root !== 'user'
            ) {
              return false;
            }
            if (root === 'places' && query.queryKey[1] === 'search') return false;
            return defaultShouldDehydrateQuery(query);
          },
        },
      }}
    >
      {children}
      {process.env.NODE_ENV === 'development' && <ReactQueryDevtoolsLazy />}
    </PersistQueryClientProvider>
  );
}
